import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
// hooks
import { useSnackbar } from 'notistack'
import { useShowModal } from 'hooks/useShowModal'
import { useDashboardsAPI } from 'hooks/useDashboardsAPI'
// constants
import { Modals } from 'constants/modals'
// views
import { SettingsIcon } from './views'
import {
  Button,
  EditIcon,
  TrashIcon,
  ButtonsWrapper
} from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/Properties/components/Property/view'
// types
import { IDashboard } from 'types/graphql'
// store
import { currentDashboardSelector, removeDashboard, setDashboardsData } from 'store/reducers/dashboards'
// utils
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

interface Props {
  dashboard: IDashboard
  closePopup: () => void
  onDashboardEditDone: (id: string) => void
}

const ControlButtons: React.FC<Props> = ({ dashboard, closePopup, onDashboardEditDone }) => {
  const dispatch = useDispatch()

  const { deleteDashboard } = useDashboardsAPI()

  const { showModal } = useShowModal({ modalType: Modals.DASHBOARD_SETTINGS_MODAL })
  const { showModal: showDelete } = useShowModal({ modalType: Modals.CONFIRM_DELETE_DASHBOARD_MODAL })

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const currentDashboard = useSelector(currentDashboardSelector)

  const onEditDone = () => {
    onDashboardEditDone(dashboard.id)
    dispatch(setDashboardsData({ edit: false }))
  }

  const onEditClick = (event) => {
    event.stopPropagation()
    closePopup()

    if (currentDashboard?.id !== dashboard.id) {
      dispatch(setDashboardsData({ current: dashboard }))
    }

    enqueueSnackbar(`Editing "${dashboard.title}"`, {
      key: 'dashboardEdit',
      variant: 'info',
      action: (key) => getSnackbarCloseButton(key, closeSnackbar, 'DONE', onEditDone),
      autoHideDuration: null
    })

    dispatch(setDashboardsData({ edit: true }))
  }

  const onDeleteDashboard = async () => {
    await deleteDashboard({ id: dashboard.id })

    dispatch(removeDashboard(dashboard.id))

    if (currentDashboard?.id === dashboard.id) {
      dispatch(setDashboardsData({ current: null }))
    }

    closePopup()
  }

  const onDeleteClick = (event) => {
    event.stopPropagation()
    closePopup()

    showDelete({
      handleConfirm: () => onDeleteDashboard()
    })
  }

  const onSettingsClick = (event) => {
    event.stopPropagation()
    closePopup()

    showModal({ dashboard })
  }

  return (
    <ButtonsWrapper>
      <Button data-testid='dashboard-edit' onClick={onEditClick}>
        <EditIcon />
      </Button>
      <Button onClick={onSettingsClick}>
        <SettingsIcon />
      </Button>
      <Button onClick={onDeleteClick}>
        <TrashIcon />
      </Button>
    </ButtonsWrapper>
  )
}

export default ControlButtons
